// 좌표로 지도 provider 를 고른다 — 국내는 네이버, 해외는 구글.
//
// ⚠️ **같은 판정이 세 벌 있다.** bbox 나 제외 규칙을 바꾸면 셋을 함께 고쳐야 한다:
//   - apps/nnb-angular/src/app/util/geo.ts   (이 파일)
//   - apps/nnb-nextjs/src/shared/lib/geo.ts  (원본)
//   - apps/nnb-partner/src/shared/lib/geo.ts
// 같은 상품을 어드민(여기)과 상세 화면(Next.js)이 각각 그리는데 판정이 갈리면, 담당자가
// 어드민에서 본 지도와 손님이 보는 지도가 달라진다.
//
// ⚠️ 사각형은 한반도 모양이 아니다 — 규슈·쓰시마는 EXCLUDE_SOUTHEAST 로 잘라냈지만
// **북한은 여전히 국내로 판정된다**(서울과 백령도 사이라 사각형으로 분리 불가). 감수한 한계다.

/**
 * 한국 영토를 감싸는 경계 상자.
 *
 * 국토 끝단이 빠지지 않도록 실측값으로 잡았다 — 마라도 33.11, 백령도 124.63, 독도 131.86,
 * 고성 통일전망대 38.61.
 */
export const KOREA_BBOX = {
  minLat: 33.0,
  maxLat: 38.7,
  minLon: 124.5,
  maxLon: 132.0,
} as const;

/**
 * bbox 남동쪽에서 잘라내는 구역 — **쓰시마와 규슈**. 이 선이 없으면 후쿠오카·시모노세키가
 * 국내로 판정돼 네이버 지도를 받는데, 네이버는 일본 타일을 못 그려 회색 지도가 된다.
 * 34.9 는 부산 태종대(35.05)·가덕도(34.98)와 쓰시마 와니우라(34.71) 사이를 지난다.
 */
const EXCLUDE_SOUTHEAST = { maxLat: 34.9, minLon: 129.0 } as const;

export type MapProvider = 'naver' | 'google';

/**
 * 지도를 그릴 수 있는 좌표인가.
 *
 * `(0,0)` 을 좌표 없음으로 보는 것이 핵심이다. 네이버 지오코딩이 해외 주소를 못 찾으면 폼이 0 을
 * 남긴 채 저장해 왔고, 운영 DB 에 그런 행이 112건 있다(2026-08-16). Null Island 는 기니만
 * 공해상이라 상품이 존재할 수 없으므로 유실 위험 없이 걸러낼 수 있다.
 *
 * 단, **둘 다 0 인 경우만** 거른다. 한쪽만 0 인 좌표는 적도나 본초자오선 위의 진짜 위치다.
 */
export function hasUsableCoords(lat: number | null | undefined, lon: number | null | undefined): boolean {
  if (typeof lat !== 'number' || typeof lon !== 'number') return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  return lat !== 0 || lon !== 0;
}

/** 좌표가 한국 안인가. **좌표가 없으면 false** — "해외"가 아니라 "국내라고 말할 수 없음"이다. */
export function isDomesticCoords(lat: number | null | undefined, lon: number | null | undefined): boolean {
  if (!hasUsableCoords(lat, lon)) return false;
  const inBox =
    lat >= KOREA_BBOX.minLat && lat <= KOREA_BBOX.maxLat && lon >= KOREA_BBOX.minLon && lon <= KOREA_BBOX.maxLon;
  if (!inBox) return false;
  // 쓰시마·규슈는 bbox 안이지만 일본이다(위 EXCLUDE_SOUTHEAST 설명).
  return !(lat < EXCLUDE_SOUTHEAST.maxLat && lon > EXCLUDE_SOUTHEAST.minLon);
}

/**
 * 좌표에 맞는 지도 provider. **좌표가 없으면 `null` — 지도를 그리지 말라는 뜻이다.**
 *
 * 판정과 사용 가능 여부를 한 함수로 합친 이유는 오용을 막기 위해서다. 두 함수로 나눠 두면
 * 호출부가 `isDomestic(...) ? 네이버 : 구글` 로만 쓰다가 좌표 없는 상품을 조용히 구글로 보낸다.
 */
export function resolveMapProvider(lat: number | null | undefined, lon: number | null | undefined): MapProvider | null {
  if (!hasUsableCoords(lat, lon)) return null;
  return isDomesticCoords(lat, lon) ? 'naver' : 'google';
}
